import { useStore } from '@nanostores/react'
import { type JSX, type SVGProps, useMemo } from 'react'

import { TwemojiFlagAustralia } from '@/components/atoms/icons/TwemojiFlagAustralia'
import TwemojiFlagAustria from '@/components/atoms/icons/TwemojiFlagAustria'
import { TwemojiFlagBelgium } from '@/components/atoms/icons/TwemojiFlagBelgium'
import TwemojiFlagCanada from '@/components/atoms/icons/TwemojiFlagCanada'
import { TwemojiFlagChina } from '@/components/atoms/icons/TwemojiFlagChina'
import TwemojiFlagForFlagIsrael from '@/components/atoms/icons/TwemojiFlagForFlagIsrael'
import TwemojiFlagForFlagUnitedKingdom from '@/components/atoms/icons/TwemojiFlagForFlagUnitedKingdom'
import TwemojiFlagForFlagUnitedStates from '@/components/atoms/icons/TwemojiFlagForFlagUnitedStates'
import TwemojiFlagFrance from '@/components/atoms/icons/TwemojiFlagFrance'
import TwemojiFlagGermany from '@/components/atoms/icons/TwemojiFlagGermany'
import { TwemojiFlagIreland } from '@/components/atoms/icons/TwemojiFlagIreland'
import TwemojiFlagNetherlands from '@/components/atoms/icons/TwemojiFlagNetherlands'
import { TwemojiFlagPoland } from '@/components/atoms/icons/TwemojiFlagPoland'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select'
import { useScopedI18n } from '@/locales/client'
import type { CountryCode } from '@/services/schemas'
import { $countries } from '@/stores/Countries'
import { $country } from '@/stores/Country'
import { cn } from '@/util/cn'

const flags: Record<CountryCode | string, (props: SVGProps<SVGSVGElement>) => JSX.Element> = {
  AT: TwemojiFlagAustria,
  CA: TwemojiFlagCanada,
  DE: TwemojiFlagGermany,
  FR: TwemojiFlagFrance,
  GB: TwemojiFlagForFlagUnitedKingdom,
  IL: TwemojiFlagForFlagIsrael,
  NL: TwemojiFlagNetherlands,
  US: TwemojiFlagForFlagUnitedStates,
  AU: TwemojiFlagAustralia,
  CN: TwemojiFlagChina,
  PL: TwemojiFlagPoland,
  BE: TwemojiFlagBelgium,
  IE: TwemojiFlagIreland,
}

const SelectTriggerValue = ({ value }: { value: string | undefined }) => {
  const t = useScopedI18n('countrySelect')
  const selectedCountry = value
  const Flag = useMemo(() => selectedCountry && flags[selectedCountry] ? flags[selectedCountry] : null, [selectedCountry])

  return (
    <div className="flex flex-row items-center gap-2 text-rsf-grey-dark placeholder:text-rsf-grey">
      {Flag && (
        <Flag className="hidden size-5 sm:block" />
      )}
      <SelectValue placeholder={t('placeholder')} className="text-rsf-grey-dark placeholder:text-rsf-grey" />
    </div>
  )
}

type Props = {
  name?: string
  className?: string
  value: string | undefined
  handleChange: (value: CountryCode) => void
}

export function CountrySelectRaw({ name, className, handleChange, value }: Props) {
  const countries = useStore($countries)

  return (
    <Select onValueChange={handleChange} value={value}>
      <SelectTrigger
        name={name}
        className={cn('col-span-2 mt-auto *:text-left', className)}
      >
        <SelectTriggerValue value={value} />
      </SelectTrigger>
      <SelectContent className="z-[60]">
        {countries && countries.map(country => (
          <SelectItem
            key={country.id}
            value={country.isocode2}
            className="cursor-pointer"
          >
            {country.countryname}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function CountrySelect() {
  const selectedCountry = useStore($country)
  const handleChange = (value: CountryCode) => {
    $country.set(value)
  }

  return (
    <CountrySelectRaw
      handleChange={handleChange}
      value={selectedCountry || undefined}
    />
  )
}

export default CountrySelect
